import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
//* MUI
import { Box } from '@mui/material';
//* Components
import AccountStatus from '../AccountDetails/AccountStatus';
import PrimaryButton from '../../Common/Buttons/PrimaryButton/PrimaryButton';
import CustomSwitch from '../../Common/Switch/CustomSwitch';
import CancelButton from '../../Common/Buttons/CancelButton';
import UpdatePasswordForm from './UpdatePasswordForm';
import ProfileDetailsPopup from '../ProfileDetailsPopup/ProfileDetailsPopup';
import Loader from '../../Common/Loader';
//* Requests
import { SetPlayermarketingPropQuery } from '../../../queries/player';
import {
  GetAccountSecurityDetailsQuery,
  StartKycRequest,
  StartKycWithDetailsRequest
} from '../../../queries/profile';
//* Context
import { AppContext, InitialStateType } from '../../../AppContext';
//* Enums
import { Actions } from '../../../enums/ActionEnums';
import { AppConsts } from '../../../enums/AppConsts';
import { Query } from '../../../enums/RequestEnums';
//* Utils
import { handleError } from '../../../utils/ui';
//* Models
import { AccountDetailsStatuses, UserModel } from '../../../models/account';
import { ActionType } from '../../../models/appcontext';
import { AccountSecurityDetailsModel, DepositDetailsModel } from '../../../models/profile';
//* Styles
import './SecurityAndSettings.scss';

const SecurityAndSettings: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dispatch, state } = useContext(AppContext);
  const getAccountDetails = useQuery({
    queryKey: Query.GetPlayerSecurityDetails,
    queryFn: GetAccountSecurityDetailsQuery,
    onSuccess: ({ data }) => {
      setFirstName(data.firstName);
      setLastName(data.lastName);
      setVerificationStatus((data as AccountSecurityDetailsModel).verificationStatus);
      setEnableEmailNotifications((data as AccountSecurityDetailsModel).agreedEmailPromotions);
      setUserId((data as AccountSecurityDetailsModel).id);
    },
    onError: ({ response }) => handleError(response, dispatch)
  });
  const [verificationStatus, setVerificationStatus] = useState(
    getAccountDetails.data?.data.verificationStatus || ''
  );
  const [openForm, setOpenForm] = useState(false);
  const [userId, setUserId] = useState<string>(state.user.data?.id || '');
  const [enableEmailNotifications, setEnableEmailNotifications] = useState<boolean>(
    state.user.data?.agreedEmailPromotions || false
  );
  const [firstName, setFirstName] = useState(getAccountDetails.data?.data.firstName || '');
  const [lastName, setLastName] = useState(getAccountDetails.data?.data.lastName || '');
  const [playerDetails, setPlayerDetails] = useState<DepositDetailsModel | null>(null);
  const [openPlayerDetailsPopup, setOpenPlayerDetailsPopup] = useState(false);

  const showUserDetailsPopUp = firstName === '' || lastName === '';

  const playerMarketingPrefQuery = useMutation(SetPlayermarketingPropQuery, {
    onSuccess: ({ data }) => {
      const token = data.accessToken as string;
      const newEmailNotificationsPreference = !enableEmailNotifications;
      updateUserInState(state, dispatch, token, {
        agreedEmailPromotions: newEmailNotificationsPreference
      });
      setEnableEmailNotifications(newEmailNotificationsPreference);
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'success',
          text: `${
            newEmailNotificationsPreference
              ? t('myAccount.accountPage.emailNotificationsEnabledMessage')
              : t('myAccount.accountPage.emailNotificationsDisabledMessage')
          }`
        }
      });
    },
    onError: ({ response }) => handleError(response, dispatch),
    onSettled: () => {
      queryClient.invalidateQueries('SetPlayermarketingPropQuery');
      queryClient.invalidateQueries(['GetAccountSecurityDetailsQuery']);
    }
  });

  const updateUserInState = (
    state: InitialStateType,
    dispatch: React.Dispatch<ActionType>,
    token: string,
    updatedFields: Partial<UserModel>
  ) => {
    const currentUserData = state.user.data as UserModel;
    const newUserData = { ...currentUserData, ...updatedFields };
    dispatch({
      type: Actions.SetUser,
      payload: {
        data: newUserData,
        token: token
      }
    });
    localStorage.setItem(AppConsts.AccessToken, token);
  };

  const handleMarketingPreferenceChange = () => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `${t('myAccount.accountPage.emailNotificationsConfirmationMessage')} ?`,
        agreeAction: marketingPreferenceConfirmed
      }
    });
  };

  const marketingPreferenceConfirmed = () => {
    dispatch({
      type: Actions.HideConfirmation
    });
    playerMarketingPrefQuery.mutate({
      playerId: userId,
      propertyName: 'agreedEmailPromotions',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      value: !enableEmailNotifications
    });
  };

  const startKycSession = useMutation(StartKycRequest, {
    onSuccess: (data) => {
      if (data.verificationUrl !== null && data.verificationUrl?.length > 0) {
        window.location.href = data.verificationUrl;
      } else if (data.error !== null && data.error.message !== null) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            autoHide: 6000,
            severity: 'error',
            text: data.error.message
          }
        });
      }
    },
    onError: ({ response }) => handleError(response, dispatch)
  });

  const startKycSessionWitDetails = useMutation(StartKycWithDetailsRequest, {
    onSuccess: (data) => {
      if (data.verificationUrl !== null && data.verificationUrl?.length > 0) {
        window.location.href = data.verificationUrl;
      } else if (data.error !== null && data.error.message !== null) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            autoHide: 6000,
            severity: 'error',
            text: data.error.message
          }
        });
      }
    },
    onError: ({ response }) => handleError(response, dispatch)
  });

  const handleVerify = () => {
    if (showUserDetailsPopUp && playerDetails === null) {
      setPlayerDetails({
        firstName: '',
        lastName: '',
        country: '',
        phoneNumber: '',
        address: '',
        city: '',
        postCode: '',
        currency: ''
      });
      setOpenPlayerDetailsPopup(true);
    } else {
      startKycSession.mutate();
    }
  };

  const handleSubmitDetails = () => {
    startKycSessionWitDetails.mutate(playerDetails as DepositDetailsModel);
  };

  return (
    <Box className="settings-container">
      <Box className="settings-container__title">{'Security & Settings'}</Box>
      <Box className="settings-container__row-container">
        <Box className="settings-container__row-container__text-box">{'Verification'}</Box>
        <Box className="settings-container__row-container__status-button-box">
          <Box>
            <AccountStatus />
          </Box>
          {verificationStatus !== AccountDetailsStatuses.Verified && (
            <Box className="settings-container__row-container--verify-button-box-row">
              <PrimaryButton
                onClick={handleVerify}
                text={t('wallet.withdrawPage.verifyAccountButton') as string}
                className="verify-button"
                disabled={startKycSession.isLoading}
                isLoading={startKycSession.isLoading}
              />
            </Box>
          )}
        </Box>
      </Box>
      {verificationStatus !== AccountDetailsStatuses.Verified && (
        <Box className="settings-container__row-container--verify-button-box-column">
          <PrimaryButton
            onClick={handleVerify}
            text={t('wallet.withdrawPage.verifyAccountButton') as string}
            className="verify-button"
            disabled={startKycSession.isLoading}
            isLoading={startKycSession.isLoading}
          />
        </Box>
      )}
      <Box className="settings-container__horizontal-line" />
      <Box className="settings-container__row-container">
        <Box className="settings-container__row-container__text-box">{'Email Notifications'}</Box>
        <Box>
          <CustomSwitch
            checked={enableEmailNotifications as boolean}
            handleChecked={() => handleMarketingPreferenceChange()}
          />
        </Box>
      </Box>
      <Box className="settings-container__horizontal-line" />
      <Box className="settings-container__row-container-password">
        <Box className="settings-container__row-container-password__text-box">{'Password'}</Box>
        <Box className="settings-container__row-container-password__cancel-button-box">
          <CancelButton
            text={t('myAccount.accountPage.passwordPage.actionButton') as string}
            onClick={() => setOpenForm(true)}
            additionalStyle={{
              color: 'common.white',
              height: '42px !important',
              padding: '10px 32px !important',
              border: '2px solid #FFF !important'
            }}
          />
        </Box>
      </Box>
      {openForm && <UpdatePasswordForm handleClose={() => setOpenForm(false)} />}
      {openPlayerDetailsPopup && (
        <ProfileDetailsPopup
          open={openPlayerDetailsPopup}
          onClose={() => {
            setOpenPlayerDetailsPopup(false);
            setPlayerDetails(null);
          }}
          playerDetails={playerDetails as DepositDetailsModel}
          setPlayerDetails={setPlayerDetails}
          onSubmit={handleSubmitDetails}
        />
      )}
      <Loader loading={startKycSessionWitDetails.isLoading} />
    </Box>
  );
};

export default SecurityAndSettings;
